/**
 * Client d'API — ADR-05.
 *
 * Authentification par COOKIES de session Sanctum : aucun jeton en JavaScript,
 * donc rien à exfiltrer par XSS. `credentials: 'include'` suffit, et l'origine
 * est la même (nginx sert la SPA et proxie /api), donc aucun CORS.
 */

const BASE: string =
  typeof import.meta.env['VITE_API_BASE_URL'] === 'string'
    ? import.meta.env['VITE_API_BASE_URL']
    : '/api/v1'

const PREFIXE = '/api/v1'

/**
 * Le jeton CSRF se retire à la racine, hors du préfixe de version : c'est
 * Sanctum qui pose cette route, pas nous.
 */
const CSRF_URL = `${BASE.endsWith(PREFIXE) ? BASE.slice(0, -PREFIXE.length) : ''}/sanctum/csrf-cookie`

const CSRF_COOKIE = 'XSRF-TOKEN'

/** Méthodes que Laravel considère comme sûres : aucune vérification CSRF. */
const SANS_ETAT = new Set(['GET', 'HEAD', 'OPTIONS'])

function lireCookie(nom: string): string | null {
  for (const brut of document.cookie.split('; ')) {
    const separateur = brut.indexOf('=')

    if (separateur > 0 && brut.slice(0, separateur) === nom) {
      return decodeURIComponent(brut.slice(separateur + 1))
    }
  }

  return null
}

/**
 * Le jeton CSRF, en le demandant au serveur s'il manque.
 *
 * Sanctum le dépose dans un cookie LISIBLE (XSRF-TOKEN, sans HttpOnly) que
 * l'on renvoie en en-tête. Le cookie de SESSION, lui, reste HttpOnly : c'est
 * la dissociation des deux qui protège, pas le secret du jeton.
 */
async function jetonCsrf(forcer = false): Promise<string | null> {
  if (!forcer) {
    const connu = lireCookie(CSRF_COOKIE)

    if (connu !== null) return connu
  }

  await fetch(CSRF_URL, { credentials: 'include', headers: { Accept: 'application/json' } })

  return lireCookie(CSRF_COOKIE)
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`HTTP ${status}`)
    this.name = 'ApiError'
  }
}

/**
 * Le motif de la dernière déconnexion subie — [DECISIONS §157, §167].
 *
 * > « La session fermée doit **dire pourquoi** : un écran qui se déconnecte
 * > sans raison passe pour une panne. »
 *
 * **Une variable de module, et non un état React.** La déconnexion peut tomber
 * sur n'importe quelle requête, y compris pendant le rendu d'un écran qui va
 * disparaître : au moment où l'écran de connexion se monte, il n'existe plus
 * aucun composant pour lui transmettre l'information. Le message doit donc
 * survivre au démontage de tout l'arbre — c'est exactement ce qu'une variable
 * de module fait, et rien d'autre ici ne le fait.
 *
 * Il ne survit PAS au rechargement de la page, et c'est voulu : un candidat qui
 * revient une heure plus tard n'a pas à relire pourquoi il a été déconnecté.
 */
let motifDeDeconnexion: string | null = null

export function raisonDeLaDeconnexion(): string | null {
  return motifDeDeconnexion
}

export function oublierLaDeconnexion(): void {
  motifDeDeconnexion = null
}

/**
 * Retient le motif quand le serveur ferme la session pour cause d'autre
 * appareil. Les autres 401 — session expirée, jamais connecté — n'en portent
 * aucun : leur écrire un message inventé serait pire que le silence.
 */
function retenirLeMotif(corps: unknown): void {
  if (typeof corps !== 'object' || corps === null) return

  const { code, message } = corps as { code?: unknown; message?: unknown }

  if (code === 'session_ailleurs' && typeof message === 'string') {
    motifDeDeconnexion = message
  }
}

/**
 * La maintenance, aperçue au vol — [DECISIONS §196], RG-9.11.
 *
 * **Même patron que `motifDeDeconnexion` ci-dessus, et pour la même raison.**
 * Une fenêtre peut s'ouvrir pendant n'importe quelle requête, y compris celle
 * d'un écran qui n'a aucun moyen d'en parler — la remise d'une épreuve, par
 * exemple, dont le composant ne rend rien de son erreur. Sans ce report, le
 * candidat voit un bouton qui cesse de tourner et rien d'autre : il conclut à
 * une panne, ferme l'onglet, et c'est là seulement que sa copie meurt.
 *
 * **On RETIENT, on ne décide pas.** L'erreur est levée comme avant : aucun
 * appelant ne change de comportement. C'est la coque qui écoute et qui affiche
 * le bandeau — sans rien démonter, ce que RG-9.12 exige précisément pour ne
 * pas détruire l'épreuve qu'on cherche à sauver.
 */
export interface MaintenanceObservee {
  motif: string | null
  repriseA: string | null
  enRetard: boolean
  /**
   * L'instant de l'observation.
   *
   * **Il départage les deux sources.** La route publique répond aussi, et elle
   * peut répondre « aucune » — parce que la reprise a eu lieu entre-temps.
   * Sans horodatage, un 503 vieux d'une heure ferait afficher une maintenance
   * finie, ce qui est une panne inventée.
   */
  vueA: number
}

let maintenanceVue: MaintenanceObservee | null = null

const temoinsDeMaintenance = new Set<(vue: MaintenanceObservee) => void>()

export function maintenanceObservee(): MaintenanceObservee | null {
  return maintenanceVue
}

/** S'abonner aux 503 de maintenance. Rend la fonction de désabonnement. */
export function auSignalDeMaintenance(temoin: (vue: MaintenanceObservee) => void): () => void {
  temoinsDeMaintenance.add(temoin)

  return () => {
    temoinsDeMaintenance.delete(temoin)
  }
}

/** La reprise a eu lieu : on oublie, sinon le bandeau survivrait au retour. */
export function oublierLaMaintenance(): void {
  maintenanceVue = null
}

/**
 * Ne retient QUE les 503 qui se disent maintenance.
 *
 * Un 503 sans ce code vient d'ailleurs — un mandataire saturé, un conteneur qui
 * redémarre. Afficher « nous revenons à 2 h 30 » devant une panne subie serait
 * une promesse qu'on n'a pas faite, et qu'on ne tiendra pas.
 */
function retenirLaMaintenance(corps: unknown): void {
  if (typeof corps !== 'object' || corps === null) return

  const { code, motif, reprise_a: reprise, en_retard: retard } = corps as Record<string, unknown>

  if (code !== 'maintenance') return

  maintenanceVue = {
    motif: typeof motif === 'string' ? motif : null,
    repriseA: typeof reprise === 'string' ? reprise : null,
    enRetard: retard === true,
    vueA: Date.now(),
  }

  temoinsDeMaintenance.forEach((temoin) => {
    if (maintenanceVue !== null) temoin(maintenanceVue)
  })
}

/**
 * @param path chemin relatif à `/api/v1`
 * @param urlComplete URL ABSOLUE à appeler telle quelle, quand elle ne peut
 *   pas être reconstruite — un lien signé perdrait sa signature au moindre
 *   réassemblage de ses paramètres (RG-1.3).
 */
export async function api<T>(
  path: string,
  init?: RequestInit,
  urlComplete?: string,
): Promise<T> {
  // `Headers` gere la fusion correctement quelle que soit la forme passee par
  // l'appelant : objet simple, tableau de paires, ou instance de Headers.
  // Un spread d'objet produirait des indices numeriques sur un tableau, et
  // rien du tout sur un Headers — silencieusement.
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  })

  new Headers(init?.headers).forEach((value, key) => {
    headers.set(key, value)
  })

  const methode = (init?.method ?? 'GET').toUpperCase()
  const protegee = !SANS_ETAT.has(methode)

  if (protegee) {
    const jeton = await jetonCsrf()

    if (jeton !== null) headers.set('X-XSRF-TOKEN', jeton)
  }

  const cible = urlComplete ?? `${BASE}${path}`

  const envoyer = async (): Promise<Response> =>
    fetch(cible, { ...init, credentials: 'include', headers })

  let res = await envoyer()

  // 419 : le jeton a expiré avec la session (deux heures). Le candidat n'a
  // pas à recommencer sa saisie parce qu'il a laissé l'onglet ouvert — on
  // renouvelle une fois, puis on renonce.
  if (res.status === 419 && protegee) {
    const jeton = await jetonCsrf(true)

    if (jeton !== null) {
      headers.set('X-XSRF-TOKEN', jeton)
      res = await envoyer()
    }
  }

  if (!res.ok) {
    const corps: unknown = await res.json().catch(() => null)

    // §167 — la seule 401 qui porte un motif. Retenue AVANT de lever : l'appelant
    // qui attrape l'erreur démonte souvent l'arbre entier, et plus personne
    // n'est là pour lire le corps.
    if (res.status === 401) retenirLeMotif(corps)

    // §196 — la fenêtre s'est ouverte pendant qu'on travaillait. Retenue
    // AVANT de lever, comme ci-dessus : l'appelant qui attrape l'erreur peut
    // n'en rien faire, et c'est justement le cas d'`Epreuve.tsx`.
    if (res.status === 503) retenirLaMaintenance(corps)

    throw new ApiError(res.status, corps)
  }

  // 204 : la déconnexion ne renvoie pas de corps, et `json()` échouerait.
  if (res.status === 204) return undefined as T

  return (await res.json()) as T
}
